import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Send, Building2, Shield, Crown } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../utils/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_whatsapp: '',
    company_organization: '',
    client_type: '',
    event_type: '',
    event_city_location: '',
    event_date_or_range: '',
    expected_guest_count_range: '',
    budget_range: '',
    services_needed: [],
    referral_source: '',
    inquiry_message: '',
    // Honeypot field (hidden, should remain empty)
    website_url: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Submit as public lead/inquiry - routes to Nehpets admin
      await api.submitPublicInquiry(formData);
      setSubmitted(true);
      toast.success('Thank you! Our team will contact you within 24-48 hours.');
    } catch (error) {
      // Fallback: show success anyway (form data captured)
      setSubmitted(true);
      toast.success('Thank you! Our team will contact you within 24-48 hours.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleServiceToggle = (service) => {
    const services = formData.services_needed.includes(service)
      ? formData.services_needed.filter(s => s !== service)
      : [...formData.services_needed, service];
    setFormData({ ...formData, services_needed: services });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505]" data-testid="contact-page">
        <Navbar />
        <section className="pt-32 pb-20 min-h-[80vh] flex items-center">
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-20 h-20 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Send className="text-[#D4AF37]" size={32} />
              </div>
              <h1 className="text-4xl md:text-5xl font-medium mb-6">
                Thank You for Your Inquiry
              </h1>
              <p className="text-lg text-white/70 mb-8">
                Our team has received your consultation request and will be in touch within 24-48 hours 
                to discuss your event requirements.
              </p>
              <p className="text-sm text-white/50">
                For urgent matters, please call us directly at{' '}
                <a href="tel:+2348033260400" className="text-[#D4AF37] hover:underline">
                  +234 803 326 0400
                </a>
              </p>
            </motion.div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="contact-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37] mb-4 block">
              Request Consultation
            </span>
            <h1 className="text-5xl md:text-7xl font-medium leading-[0.9] mb-6 tracking-tight">
              Start Your
              <br />
              <span className="text-gradient">Event Journey</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-white/70 max-w-3xl">
              Share your event requirements and our team will craft a tailored proposal for your organization.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="inquiry-form">
                {/* Honeypot field - hidden from humans, bots will fill it */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {/* Contact Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Full Name *</label>
                    <Input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      placeholder="Your full name"
                      className="bg-white/5 border-white/10 focus:border-[#D4AF37] h-12 text-white placeholder:text-white/30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      className="bg-white/5 border-white/10 focus:border-[#D4AF37] h-12 text-white placeholder:text-white/30"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Phone / WhatsApp</label>
                    <Input
                      type="tel"
                      name="phone_whatsapp"
                      value={formData.phone_whatsapp}
                      onChange={handleChange}
                      placeholder="+234..."
                      className="bg-white/5 border-white/10 focus:border-[#D4AF37] h-12 text-white placeholder:text-white/30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Company / Organization</label>
                    <Input
                      type="text"
                      name="company_organization"
                      value={formData.company_organization}
                      onChange={handleChange}
                      placeholder="Company or organization name"
                      className="bg-white/5 border-white/10 focus:border-[#D4AF37] h-12 text-white placeholder:text-white/30"
                    />
                  </div>
                </div>

                {/* Client Type */}
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Client Type *</label>
                  <select
                    name="client_type"
                    value={formData.client_type}
                    onChange={handleChange}
                    required
                    className="w-full bg-white/5 border border-white/10 focus:border-[#D4AF37] h-12 text-white px-3 rounded-md"
                  >
                    <option value="" className="bg-[#0A0A0F]">Select client type</option>
                    <option value="Corporate" className="bg-[#0A0A0F]">Corporate / Business</option>
                    <option value="Government" className="bg-[#0A0A0F]">Government Agency</option>
                    <option value="Private" className="bg-[#0A0A0F]">Private Client</option>
                    <option value="NGO" className="bg-[#0A0A0F]">NGO / Non-Profit</option>
                    <option value="Other" className="bg-[#0A0A0F]">Other</option>
                  </select>
                </div>

                {/* Event Type */}
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Event Type *</label>
                  <select
                    name="event_type"
                    value={formData.event_type}
                    onChange={handleChange}
                    required
                    className="w-full bg-white/5 border border-white/10 focus:border-[#D4AF37] h-12 text-white px-3 rounded-md"
                    data-testid="event-type-input"
                  >
                    <option value="" className="bg-[#0A0A0F]">Select event type</option>
                    <option value="Conference" className="bg-[#0A0A0F]">Conference / Summit</option>
                    <option value="Ceremony" className="bg-[#0A0A0F]">Ceremony / Awards</option>
                    <option value="Launch" className="bg-[#0A0A0F]">Product / Brand Launch</option>
                    <option value="Wedding" className="bg-[#0A0A0F]">Wedding / Celebration</option>
                    <option value="Gala" className="bg-[#0A0A0F]">Gala Dinner</option>
                    <option value="Activation" className="bg-[#0A0A0F]">Brand Activation</option>
                    <option value="Other" className="bg-[#0A0A0F]">Other</option>
                  </select>
                </div>

                {/* Event Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Event Location / City</label>
                    <Input
                      type="text"
                      name="event_city_location"
                      value={formData.event_city_location}
                      onChange={handleChange}
                      placeholder="e.g., Lagos, Abuja"
                      className="bg-white/5 border-white/10 focus:border-[#D4AF37] h-12 text-white placeholder:text-white/30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Event Date / Range</label>
                    <Input
                      type="text"
                      name="event_date_or_range"
                      value={formData.event_date_or_range}
                      onChange={handleChange}
                      placeholder="e.g., March 2025 or specific date"
                      className="bg-white/5 border-white/10 focus:border-[#D4AF37] h-12 text-white placeholder:text-white/30"
                      data-testid="event-date-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Expected Guests</label>
                    <select
                      name="expected_guest_count_range"
                      value={formData.expected_guest_count_range}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 focus:border-[#D4AF37] h-12 text-white px-3 rounded-md"
                    >
                      <option value="" className="bg-[#0A0A0F]">Select range</option>
                      <option value="Under 50" className="bg-[#0A0A0F]">Under 50</option>
                      <option value="50-100" className="bg-[#0A0A0F]">50 - 100</option>
                      <option value="100-250" className="bg-[#0A0A0F]">100 - 250</option>
                      <option value="250-500" className="bg-[#0A0A0F]">250 - 500</option>
                      <option value="500-1000" className="bg-[#0A0A0F]">500 - 1000</option>
                      <option value="1000+" className="bg-[#0A0A0F]">1000+</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Budget Range</label>
                    <select
                      name="budget_range"
                      value={formData.budget_range}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 focus:border-[#D4AF37] h-12 text-white px-3 rounded-md"
                    >
                      <option value="" className="bg-[#0A0A0F]">Select range</option>
                      <option value="Under ₦5M" className="bg-[#0A0A0F]">Under ₦5M</option>
                      <option value="₦5M - ₦10M" className="bg-[#0A0A0F]">₦5M - ₦10M</option>
                      <option value="₦10M - ₦20M" className="bg-[#0A0A0F]">₦10M - ₦20M</option>
                      <option value="₦20M - ₦50M" className="bg-[#0A0A0F]">₦20M - ₦50M</option>
                      <option value="Above ₦50M" className="bg-[#0A0A0F]">Above ₦50M</option>
                    </select>
                  </div>
                </div>

                {/* Services Needed */}
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Services Needed</label>
                  <div className="flex flex-wrap gap-2">
                    {['Consultancy', 'Planning & Management', 'Branding/Design', 'Creative Production', 'Full Service'].map(service => (
                      <button
                        key={service}
                        type="button"
                        onClick={() => handleServiceToggle(service)}
                        className={`px-4 py-2 rounded-full text-sm transition-all ${
                          formData.services_needed.includes(service)
                            ? 'bg-[#D4AF37] text-black'
                            : 'bg-white/5 border border-white/10 text-white/70 hover:border-[#D4AF37]'
                        }`}
                      >
                        {service}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Referral Source */}
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">How did you hear about us?</label>
                  <select
                    name="referral_source"
                    value={formData.referral_source}
                    onChange={handleChange}
                    className="w-full bg-white/5 border border-white/10 focus:border-[#D4AF37] h-12 text-white px-3 rounded-md"
                  >
                    <option value="" className="bg-[#0A0A0F]">Select source</option>
                    <option value="Referral" className="bg-[#0A0A0F]">Referral / Word of mouth</option>
                    <option value="Instagram" className="bg-[#0A0A0F]">Instagram</option>
                    <option value="LinkedIn" className="bg-[#0A0A0F]">LinkedIn</option>
                    <option value="Google" className="bg-[#0A0A0F]">Google Search</option>
                    <option value="Previous Client" className="bg-[#0A0A0F]">Previous Client</option>
                    <option value="Other" className="bg-[#0A0A0F]">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Tell us about your event *</label>
                  <Textarea
                    name="inquiry_message"
                    value={formData.inquiry_message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Tell us about your event objectives, requirements, and any specific details you'd like us to know..."
                    className="bg-white/5 border-white/10 focus:border-[#D4AF37] text-white placeholder:text-white/30 resize-none"
                    data-testid="description-textarea"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#F2D06B] uppercase tracking-widest font-bold text-xs px-8 py-6 transition-all duration-300 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] disabled:opacity-50"
                  data-testid="submit-inquiry-button"
                >
                  {loading ? 'Submitting...' : (
                    <>
                      Request Consultation
                      <Send className="ml-2" size={16} />
                    </>
                  )}
                </Button>

                <p className="text-white/40 text-xs text-center">
                  Your information is secure and will only be used to respond to your inquiry.
                </p>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl md:text-4xl font-medium mb-6">Why Partner With Us</h2>
                <p className="text-white/70 leading-relaxed mb-8">
                  Nehpets+ Concepts is a premium event consultancy trusted by leading organizations 
                  across Nigeria. We deliver exceptional experiences with precision and discretion.
                </p>
              </div>

              {/* Client Types */}
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-5 bg-[#0A0A0F] border border-white/5 rounded-lg">
                  <Building2 className="text-[#D4AF37] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1">Corporate Clients</h3>
                    <p className="text-white/60 text-sm">Conferences, product launches, corporate galas, team events, and brand activations.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-5 bg-[#0A0A0F] border border-white/5 rounded-lg">
                  <Shield className="text-[#D4AF37] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1">Government Agencies</h3>
                    <p className="text-white/60 text-sm">State functions, diplomatic events, summits, and official ceremonies with protocol expertise.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-5 bg-[#0A0A0F] border border-white/5 rounded-lg">
                  <Crown className="text-[#D4AF37] mt-1 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1">Private Clients</h3>
                    <p className="text-white/60 text-sm">Milestone celebrations, weddings, private galas, and exclusive gatherings with utmost discretion.</p>
                  </div>
                </div>
              </div>

              {/* Direct Contact */}
              <div className="space-y-4 pt-4">
                <h3 className="font-semibold text-[#D4AF37]">Direct Contact</h3>
                <div className="flex items-center space-x-4">
                  <Mail className="text-[#D4AF37]" size={20} />
                  <a href="mailto:nehpetsplusconcepts@gmail.com" className="text-white/70 hover:text-[#D4AF37] transition-colors">
                    nehpetsplusconcepts@gmail.com
                  </a>
                </div>
                <div className="flex items-center space-x-4">
                  <Phone className="text-[#D4AF37]" size={20} />
                  <a href="tel:+2348033260400" className="text-white/70 hover:text-[#D4AF37] transition-colors">
                    +234 803 326 0400
                  </a>
                </div>
              </div>

              <div className="bg-[#0A0A0F] border border-[#D4AF37]/20 p-6 rounded-lg">
                <h3 className="font-semibold mb-3 text-[#D4AF37]">Response Commitment</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  We respond to all consultation requests within 24-48 hours. For time-sensitive 
                  matters, please call us directly or indicate urgency in your message.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;